import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SiteNav from './blog/SiteNav';
import Seo from './blog/Seo';
import { SITE_DESCRIPTION, SITE_TITLE } from './site';
import { 
  Mail,
  ChevronRight,
  ChevronLeft,
  Server,
  Zap,
  GraduationCap,
  Cpu,
  Database,
  Layout,
  Car,
  Music,
  MapPin,
  Smartphone,
  Award,
  Users,
  Briefcase,
  Bot
} from 'lucide-react';

const GithubIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/><path d="M9 18c-4.51 2-5-2-7-2"/></svg>
);

const LinkedinIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>
);

const Typewriter = ({ texts, speed = 100, pause = 2000 }) => {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);
  const [blink, setBlink] = useState(true);

  useEffect(() => {
    if (subIndex === texts[index].length + 1 && !reverse) {
      setTimeout(() => setReverse(true), pause);
      return;
    }
    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % texts.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? speed / 2 : speed);

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, texts, speed, pause]);

  useEffect(() => {
    const timeout2 = setTimeout(() => {
      setBlink((prev) => !prev);
    }, 500);
    return () => clearTimeout(timeout2);
  }, [blink]);

  return (
    <span className="typewriter-text">
      {texts[index].substring(0, subIndex)}
      <span className={`typewriter-cursor ${blink ? 'visible' : ''}`}>|</span>
    </span>
  );
};



/** Featured proof first, then PRODUCTS → AUTOMATION → DEVOPS, then year. Jengo/CRM use sortYear to sit after DevLog. */
const PORTFOLIO_CATEGORY_ORDER = ["PRODUCTS", "AUTOMATION", "DEVOPS"];
const portfolioCategoryRank = (cat) => {
  const i = PORTFOLIO_CATEGORY_ORDER.indexOf(cat);
  return i === -1 ? PORTFOLIO_CATEGORY_ORDER.length : i;
};

const App = () => {
  const [searchParams] = useSearchParams();
  const [currentSection, setCurrentSection] = useState(0);
  const [isWorksMobile, setIsWorksMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const onChange = () => setIsWorksMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const go = searchParams.get('go');
    const map = { works: 1, services: 2, about: 3 };
    if (go && go in map) setCurrentSection(map[go]);
  }, [searchParams]);
  
  const techLogos = [
    { name: "AWS", src: "/logo-aws.svg", top: "15%", left: "10%" },
    { name: "Docker", src: "/logo-docker.png", top: "20%", right: "12%" },
    { name: "Kubernetes", src: "/logo-k8s.svg", bottom: "25%", left: "15%" },
    { name: "Terraform", src: "/logo-terraform.svg", bottom: "18%", right: "18%" },
    { name: "Python", src: "/logo-python.svg", top: "35%", right: "8%" },
    { name: "Node.js", src: "/tech-node.png", top: "40%", left: "5%" },
  ];

  const experienceStats = [
    { label: "Software Engineer", img: "/pillar-cloud.png", offset: -20 },
    { label: "DevOps Engineer", img: "/pillar-consultant.png", offset: 15 },
    { label: "Platform Engineer", img: "/pillar-workflow.png", offset: -40 },
    { label: "SRE & Cloud Architect", img: "/pillar-cloud.png", offset: 10 },
  ];



  const allProjects = [
    { id: 1, title: "Tajilabs Kenya Website", cat: "WEB", year: "2025", desc: "Company site for Tajilabs — products, case studies, and services. Built in React + Vite. | tajilabs.co.ke", img: "/images/projects/tajilabs.png", tech: ["React", "Vite", "DevOps", "CI/CD"] },
    { id: 52, title: "SNPL", cat: "WEB", year: "2026", featured: true, desc: "Inventory financing platform for retailers — stock now, pay later credit and operations portal. | stocknowpaylater.co.rw", img: "/images/projects/snpl.jpg", tech: ["React", "Vite", "Fintech", "Credit Ops"] },
    { id: 53, title: "Fuzu Ventures", cat: "WEB", year: "2026", featured: true, desc: "Corporate site for investment and ventures house — brand, trust, and conversion-focused marketing. | fuzuventures.co.ke", img: "/images/projects/fuzu-ventures.jpg", tech: ["React", "Vite", "SEO", "Vercel"] },
    { id: 54, title: "Avolution Africa", cat: "WEB", year: "2026", featured: true, desc: "Premium multi-page marketing platform for East African avocado export (Kenya & Tanzania). | avolution.africa", img: "/images/projects/avolution.jpg", tech: ["React", "Vite", "SEO", "Vercel"] },
    { id: 56, title: "Tai Frontier", cat: "WEB", year: "2026", featured: true, desc: "Marketing site for Nairobi's waste management company — services, sustainability, and project stories. | taifrontier.com", img: "/images/projects/tai-frontier.jpg", tech: ["React", "Vite", "SEO", "Vercel"] },
    { id: 55, title: "Tajilabs CRM", cat: "PRODUCTS", year: "2026", sortYear: "2024", sortId: 23, desc: "Leads, deal pipeline, email & WhatsApp campaigns, quotations and invoices. Live at crm.tajilabs.co.ke.", img: "/images/products/tajilabs-crm.png", tech: ["React", "Node.js", "WhatsApp", "Pipeline"] },
    { id: 2, title: "Lendbucks", cat: "AUTOMATION", year: "2023", desc: "Automated client onboarding system that streamlined the loan application process. | lendbucks.co.ke", img: "/images/projects/lendbucks.png", tech: ["Node.js", "Express", "PostgreSQL", "Docker"] },
    { id: 3, title: "N8N Workflow Automation", cat: "AUTOMATION", year: "2024", desc: "Business process automation platform that connects various services.", img: "/images/projects/n8n.png", tech: ["N8N", "API Integration", "Workflow Automation"] },
    { id: 4, title: "Hala Nairobi", cat: "WEB", year: "2023", desc: "Restaurant and hospitality website with online ordering and reservation system. | halanairobi.com", img: "/images/projects/hala.png", tech: ["React", "Node.js", "MongoDB", "Payment Gateway"] },
    { id: 5, title: "MSGN & Partners Website", cat: "WEB", year: "2023", desc: "Professional services company website with client portal and document management.", img: "/images/projects/msgn.png", tech: ["Laravel", "Vue.js", "MySQL", "AWS"] },
    { id: 6, title: "Nelson Haus", cat: "WEB", year: "2024", desc: "Real estate platform featuring property listings and virtual tours.", img: "/images/projects/nelson-haus.png", tech: ["Next.js", "TypeScript", "PostgreSQL", "Cloudinary"] },
    { id: 7, title: "JK Ventures Website", cat: "WEB", year: "2024", desc: "Investment platform with portfolio management and analytics dashboard.", img: "/images/projects/jk-vantures.png", tech: ["React", "Node.js", "MongoDB", "Chart.js"] },
    { id: 8, title: "Qwetu Sacco", cat: "WEB", year: "2023", desc: "Financial cooperative website with member portal and loan management. | qwetusacco.com", img: "/images/projects/qwetu-sacco.png", tech: ["PHP", "Laravel", "MySQL", "Bootstrap"] },
    { id: 9, title: "Tikvah Center", cat: "WEB", year: "2023", desc: "Educational institution website with course management and student portal.", img: "/images/projects/tikvah.png", tech: ["WordPress", "LMS Plugin", "Custom Themes", "SEO"] },
    { id: 10, title: "Gap Talent Partners", cat: "AUTOMATION", year: "2024", desc: "HR recruitment platform with cloud hosting and workflow automation.", img: "/images/projects/gap-talent-partners.png", tech: ["WordPress", "Cloud Hosting", "Automation", "CI/CD"] },
    { id: 12, title: "Amrutt Kenya E-Commerce", cat: "WEB", year: "2023", desc: "WooCommerce store with bulk SMS and WhatsApp retail integration.", img: "/images/projects/amrutt-kenya.png", tech: ["WordPress", "WooCommerce", "Bulk SMS", "WhatsApp API"] },
    { id: 13, title: "Liafro Store", cat: "SHOPIFY", year: "2023", desc: "Shopify store with DHL and Fargo logistics integrations.", img: "/images/projects/liafro-kenya.png", tech: ["Shopify", "Liquid", "Logistics APIs", "DHL"] },
    { id: 14, title: "Pension Pilot Platform", cat: "DEVOPS", year: "2024", desc: "Pension consolidation platform for tracking multiple retirement accounts.", img: "/images/projects/pension-pilot.png", tech: ["React", "Node.js", "PostgreSQL", "APIs"] },
    { id: 16, title: "Errorlytic", cat: "PRODUCTS", year: "2024", desc: "Automotive diagnostic system: fault codes, quotes, and insurance.", img: "/images/products/errorlytic.png", tech: ["Fault Detection", "Quotation", "Insurance", "Inventory"] },
    { id: 17, title: "AJiri Automation", cat: "PRODUCTS", year: "2024", desc: "HR interview automation: scheduling and candidate tracking.", img: "/images/products/ajiri.png", tech: ["Scheduling", "Calendar Sync", "Pipeline", "Tracking"] },
    { id: 18, title: "Remedy School OS", cat: "PRODUCTS", year: "2024", desc: "Full school operating system: admissions, fees, and grading.", img: "/images/products/remedy.png", tech: ["Admissions", "M-Pesa", "Timetabling", "Grades"] },
    { id: 19, title: "Jengo", cat: "PRODUCTS", year: "2026", sortYear: "2024", sortId: 22, desc: "Property management: tenants, rent collection, and maintenance. | jengo.tajilabs.co.ke", img: "/images/projects/jengo_dashboard.png", tech: ["Tenant Mgmt", "M-Pesa", "Maintenance", "Reports"] },
    { id: 20, title: "Verifay", cat: "PRODUCTS", year: "2024", desc: "High-integrity Kenyan payroll SaaS automating Finance Act compliance (NSSF, SHIF, Housing Levy) with enterprise-grade accuracy and immutable audit trails.", img: "/images/products/verifay.jpg", tech: ["Node.js", "Fastify", "MongoDB", "Redis", "Compliance"] },
    { id: 21, title: "DevLog", cat: "PRODUCTS", year: "2024", desc: "Standups that write themselves: GitHub activity surfaced in Slack.", img: "/images/products/devlog.png", tech: ["GitHub", "Slack", "PR Tracking", "Summaries"] },
    { id: 22, title: "Amrutt Kenya Headless", cat: "WEB", year: "2024", desc: "Headless e‑commerce with Elasticsearch and Cloudflare Workers.", img: "/images/projects/amrutt-headless.png", tech: ["WordPress", "Elasticsearch", "Cloudflare Workers"] },
    { id: 23, title: "AI-SDR by BetterQA", cat: "AUTOMATION", year: "2025", desc: "AI-powered sales development and outreach tooling.", img: "/images/projects/ai-sdr-betterqa.png", tech: ["AI", "Sales Automation", "BetterQA"] },
    { id: 24, title: "Betterflow", cat: "AUTOMATION", year: "2025", desc: "Workflow and process automation platform at BetterQA.", img: "/images/projects/betterflow-betterqa.png", tech: ["Workflow", "Automation", "BetterQA"] },
    { id: 25, title: "Remedy Dashboard", cat: "PRODUCTS", year: "2024", desc: "Grafana monitoring and dashboards for the Remedy platform.", img: "/images/projects/remedy-grafana.png", tech: ["Grafana", "Monitoring", "Remedy", "Metrics"] },
    { id: 26, title: "Mensbestbasics", cat: "SHOPIFY", year: "2024", desc: "UK Shopify store with custom theme and backend extensions.", img: "/images/projects/mensbestbasics-shopify.png", tech: ["Shopify", "Liquid", "E‑commerce", "Retail"] },
    { id: 27, title: "Purity Sang Advocates", cat: "WEB", year: "2024", desc: "Professional website for a Kenyan law firm.", img: "/images/projects/puritysangadvocates.png", tech: ["WordPress", "Responsive", "SEO", "Inquiry"] },
    { id: 28, title: "Fintech Group", cat: "WEB", year: "2024", desc: "Secure, data-driven financial platform. | fintech-group.com", img: "/images/projects/fintech-group.png", tech: ["WordPress", "Fintech", "Security"] },
    { id: 29, title: "Obotech Solutions", cat: "WEB", year: "2024", desc: "Corporate platform for technology solutions. | obotechsolutions.co.ke", img: "/images/projects/placeholder.png", tech: ["WordPress", "Corporate", "Custom UI"] },
    { id: 30, title: "OESAI (Insurance)", cat: "WEB", year: "2024", desc: "Insurance portal for cross-border industry solutions. | oesai.org", img: "/images/projects/oesai.png", tech: ["WordPress", "Portal", "Insurance"] },
    { id: 31, title: "Likoni Freighters", cat: "WEB", year: "2024", desc: "Logistics and freight management portal. | likonifreighters.com", img: "/images/projects/placeholder.png", tech: ["WordPress", "Logistics", "Freight"] },
    { id: 32, title: "Compact Hydro Solutions", cat: "WEB", year: "2024", desc: "Engineering solutions for hydro-power infrastructure. | compacthydrosolutions.com", img: "/images/projects/hydro.png", tech: ["WordPress", "Engineering", "Hydro"] },
    { id: 33, title: "Clifton Gas", cat: "WEB", year: "2024", desc: "Energy and gas distribution portal. | cliftongas.com", img: "/images/projects/clifton.png", tech: ["WordPress", "Energy", "Infrastructure"] },
    { id: 34, title: "Mahan Road", cat: "WEB", year: "2024", desc: "Civil engineering and road construction corporate site. | mahanroad.com", img: "/images/projects/placeholder.png", tech: ["WordPress", "Engineering", "Construction"] },
    { id: 35, title: "Edgescope", cat: "WEB", year: "2024", desc: "Technical solutions and IT consulting portal. | edgescope.co.ke", img: "/images/projects/edgescope.png", tech: ["WordPress", "Consulting", "IT"] },
    { id: 36, title: "Great Rift Coffee", cat: "WEB", year: "2024", desc: "E-commerce platform for premium coffee retail. | greatriftcoffee.com", img: "/images/projects/coffee.png", tech: ["WooCommerce", "E-commerce", "Retail"] },
    { id: 37, title: "Diro Kenya", cat: "WEB", year: "2024", desc: "Performance-optimized storefront for retail. | dirokenya.com", img: "/images/projects/diro.png", tech: ["WooCommerce", "E-commerce", "Conversion"] },
    { id: 38, title: "Maridady Motors", cat: "WEB", year: "2024", desc: "Automotive retail and import platform. | maridadymotors.com", img: "/images/projects/maridady.png", tech: ["WordPress", "Automotive", "Retail"] },
    { id: 39, title: "Chirowa Online", cat: "WEB", year: "2024", desc: "Digital marketplace and e-commerce portal. | chirowaonline.com", img: "/images/projects/chirowa.png", tech: ["WooCommerce", "Marketplace", "E-commerce"] },
    { id: 40, title: "Phenomenal Imports Global", cat: "WEB", year: "2024", desc: "Global trade and imports management platform. | phenomenalimportsglobal.com", img: "/images/projects/placeholder.png", tech: ["WordPress", "Import", "Global Trade"] },
    { id: 41, title: "Kitali Suppliers Ltd", cat: "WEB", year: "2024", desc: "Industrial and retail supply chain portal. | kitalisuppliersltd.com", img: "/images/projects/kitali.png", tech: ["WordPress", "Supply Chain", "B2B"] },
    { id: 42, title: "Mercy and Love Foundation", cat: "WEB", year: "2024", desc: "NGO platform for community outreach and engagement. | mercyandlovefoundation.org", img: "/images/projects/mercyandlove.png", tech: ["WordPress", "NGO", "Community"] },
    { id: 43, title: "Jilinde", cat: "WEB", year: "2024", desc: "Social impact and accessibility focused portal. | jilinde.org", img: "/images/projects/placeholder.png", tech: ["WordPress", "Accessibility", "NGO"] },
    { id: 44, title: "SymbioCity Kenya", cat: "WEB", year: "2024", desc: "Sustainable urban development platform. | symbiocitykenya.org", img: "/images/projects/symbiocity.png", tech: ["WordPress", "Sustainability", "Urban"] },
    { id: 45, title: "AUSPA Kenya", cat: "WEB", year: "2024", desc: "Public sector engagement and standards gateway. | auspakenya.com", img: "/images/projects/placeholder.png", tech: ["WordPress", "Public Sector", "Standards"] },
    { id: 46, title: "Heavenly Manna Kenya", cat: "WEB", year: "2024", desc: "Community engagement and outreach foundation site. | heavenlymannakenya.co.ke", img: "/images/projects/placeholder.png", tech: ["WordPress", "NGO", "Foundation"] },
    { id: 47, title: "Homeground Hospital", cat: "WEB", year: "2024", desc: "Healthcare service portal with structured data and clean UI. | homegroundhospital.org", img: "/images/projects/placeholder.png", tech: ["WordPress", "Healthcare", "UI/UX"] },
    { id: 48, title: "Vast Inner Potential", cat: "WEB", year: "2024", desc: "Professional growth and potential development portal. | vastinnerpotential.com", img: "/images/projects/placeholder.png", tech: ["WordPress", "Professional", "Coaching"] },
    { id: 49, title: "Chemagro Portal", cat: "WEB", year: "2024", desc: "Specialized subdomain for agricultural chemicals. | chemagro.obotechsolutions.com", img: "/images/projects/placeholder.png", tech: ["WordPress", "Agriculture", "Portal"] },
    { id: 50, title: "Faras Portal", cat: "WEB", year: "2024", desc: "Subdomain extension for specialized logistical services. | faras.obotechsolutions.com", img: "/images/projects/faras.png", tech: ["WordPress", "Logistics", "Portal"] },
    { id: 51, title: "Qvideodata", cat: "WEB", year: "2024", desc: "Video data management and architecture portal. | qvideodata.com", img: "/images/projects/placeholder.png", tech: ["WordPress", "Video Data", "Architecture"] }
  ];


  const [activeFilter, setActiveFilter] = useState("ALL");

  const projectsMatchingFilter =
    activeFilter === "ALL"
      ? allProjects
      : allProjects.filter((p) => p.cat === activeFilter);
  const filteredProjects = [...projectsMatchingFilter].sort((a, b) => {
    const fa = a.featured ? 0 : 1;
    const fb = b.featured ? 0 : 1;
    if (fa !== fb) return fa - fb;
    const ra = portfolioCategoryRank(a.cat);
    const rb = portfolioCategoryRank(b.cat);
    if (ra !== rb) return ra - rb;
    const ya = parseInt(a.sortYear ?? a.year, 10) || 0;
    const yb = parseInt(b.sortYear ?? b.year, 10) || 0;
    if (yb !== ya) return yb - ya;
    return (a.sortId ?? a.id ?? 0) - (b.sortId ?? b.id ?? 0);
  });

  // What you hire for (outcomes) — primary Services view
  const serviceCapabilities = [
    {
      id: "platform",
      title: "DevOps & Platform Engineering",
      blurb: "Environments, CI/CD, and cutovers so products ship the same way every week — not by heroics.",
      icon: <Server size={22} />,
      skills: ["Kubernetes", "Terraform", "GitOps", "AWS / GCP / Azure", "Internal platforms"]
    },
    {
      id: "ai-automation",
      title: "AI Automation Systems",
      blurb: "Agents and workflows that run against real ops — with keys, environments, and failure paths that hold up.",
      icon: <Bot size={22} />,
      skills: ["LLM agents", "n8n / orchestration", "RAG pipelines", "Autonomous workflows", "AI infra"]
    },
    {
      id: "cloud-arch",
      title: "Cloud Architecture & Environments",
      blurb: "Multi-env design (dev → staging → prod), network and identity baselines, and production-ready IaC.",
      icon: <Cpu size={22} />,
      skills: ["Terraform", "Ansible", "VPC / IAM", "TLS / ingress", "Environment strategy"]
    },
    {
      id: "saas",
      title: "Production SaaS Engineering",
      blurb: "APIs, multi-tenant products, and ops dashboards that operators open on Monday — not demos.",
      icon: <Layout size={22} />,
      skills: ["Node / Laravel / .NET", "React", "PostgreSQL / MongoDB", "M-Pesa flows", "Multi-tenant"]
    },
    {
      id: "workflow",
      title: "Workflow & Business Automation",
      blurb: "Replace spreadsheet loops with pipelines: approvals, notifications, sync, and audit trails.",
      icon: <Briefcase size={22} />,
      skills: ["n8n", "RPA", "GitHub Actions", "Integrations", "Ops tooling"]
    },
    {
      id: "sre",
      title: "Reliability & Observability",
      blurb: "See breakage before users do — metrics, logs, and SLOs tied to the products that matter.",
      icon: <Zap size={22} />,
      skills: ["Prometheus", "Grafana", "Loki / ELK", "SLOs", "Incident hygiene"]
    },
    {
      id: "consulting",
      title: "Systems Consulting & Leadership",
      blurb: "Greenfield platforms and delivery leadership — architecture decisions that survive the first production week.",
      icon: <Users size={22} />,
      skills: ["Architecture", "Delivery", "Team enablement", "Cutover planning", "Stakeholder ops"]
    }
  ];

  // Tooling depth — secondary view via filter
  const expertiseCategories = [
    {
      id: "devops",
      title: "DevOps & Cloud",
      blurb: "Design, ship, and operate production systems on AWS, GCP, and Azure.",
      icon: <Server size={22} />,
      groups: [
        { label: "Cloud", items: ["AWS", "GCP", "Azure"] },
        { label: "Runtime", items: ["Kubernetes", "Helm", "ArgoCD"] },
        { label: "IaC", items: ["Terraform", "Ansible", "CloudFormation", "Pulumi", "GitOps"] }
      ]
    },
    { id: "backend", title: "Backend Development", blurb: "APIs and services that stay up under real load.", icon: <Cpu size={22} />, skills: ["PHP / Laravel", "C# / .NET", "Node.js", "Python", "Go", "Java", "REST", "GraphQL"] },
    { id: "agentic-ai", title: "Agentic AI & Automation", blurb: "Agents and workflows that do the work — not demo theatre.", icon: <Bot size={22} />, skills: ["Multi-agent orchestration", "Autonomous workflows", "LLM agents", "Agentic RAG", "AI/ML infra"] },
    { id: "observability", title: "Observability & SRE", blurb: "See it break before the user does.", icon: <Zap size={22} />, skills: ["Prometheus", "Grafana", "Loki", "ELK", "DataDog", "SLOs / SLAs"] },
    { id: "frontend", title: "Frontend & Mobile", blurb: "Interfaces people actually finish using.", icon: <Layout size={22} />, skills: ["React", "Vue.js", "Next.js", "TypeScript", "Flutter", "Tailwind"] },
    { id: "data", title: "Data & Databases", blurb: "Stores and sync that operations can trust.", icon: <Database size={22} />, skills: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "DynamoDB", "Dynamics 365"] },
    { id: "automation", title: "Workflow Automation", blurb: "Pipelines so nobody copies the same spreadsheet twice.", icon: <Briefcase size={22} />, skills: ["n8n", "RPA", "Bash / Python", "GitHub Actions", "Jenkins", "Azure DevOps"] }
  ];

  const [servicesFilter, setServicesFilter] = useState("CAPABILITIES");
  const servicesCards = servicesFilter === "STACK" ? expertiseCategories : serviceCapabilities;

  const fullExperience = [
    {
      role: "Systems Engineering Lead Consultant",
      company: "Aberdair Aviation Group",
      logo: "assets/logos/aberdair.png",
      date: "Jul 2026 – Present",
      desc: "End-to-end systems engineering and DevOps for a greenfield internal technology platform — architecture, implementation, environment strategy, and production readiness.",
      points: [
        "Designed application and operating architecture spanning service-layer APIs, web delivery, relational data, access control, and auditability",
        "Implemented a multi-environment delivery model (dev, staging, UAT) with containerised workflows to cut environment drift",
        "Engineered production infrastructure as code — compute, network access controls, host bootstrap, and secure reverse-proxy/TLS termination",
        "Strengthened operational security through repository hygiene, externalised secrets, least-privilege admin, and controlled public exposure"
      ]
    },
    {
      role: "Software & DevOps Engineer (Consultant)",
      company: "BetterQA",
      logo: "assets/logos/betterqa.svg",
      date: "May 2025 – Present",
      desc: "Scaling a QA automation SaaS platform to enterprise clients — multi-tenant backend and consistent pipeline infrastructure.",
      points: [
        "Architected multi-tenant SaaS infrastructure using Terraform on AWS and GCP, onboarding enterprise clients with isolated environments",
        "Designed Kubernetes-based test orchestration with custom Python scripts — reduced test execution latency by 40%",
        "Led technical design of multi-tenant Laravel/PHP backend — tenant isolation, subscriptions, feature flags, and notifications",
        "Engineered CI/CD (GitHub Actions, Jenkins) with automated validation, zero-downtime migrations, and Prometheus/Grafana/Loki SLOs"
      ]
    },
    {
      role: "Software Engineer & DevOps Lead",
      company: "Greenspoon Kenya",
      logo: "assets/logos/greenspoon.svg",
      date: "Nov 2022 – Dec 2025",
      desc: "Directed platform engineering and product software reporting to CTO and CEO during a critical scale phase.",
      points: [
        "Built an Internal Developer Platform standardising the SDLC — reduced deployment time from hours to minutes",
        "Led full-stack delivery of Greenspoon Paperless — logistics and warehouse management digitising end-to-end fulfilment",
        "Established Terraform IaC across AWS (EC2, RDS, S3, CloudWatch) with automated backup, DR, and 99.5%+ uptime",
        "Cut server load by 60% on operational dashboards and reduced stockouts by 40% through backorder and minimum-stock algorithms"
      ]
    },
    {
      role: "AI/ML Engineer — DevOps & Backend",
      company: "Turality LLC",
      logo: "assets/logos/turality.png",
      date: "Nov 2023 – Feb 2024",
      desc: "Built high-throughput LLM inference infrastructure and backend services for an AI social media automation platform.",
      points: [
        "Engineered LLM inference pipelines in Python (FastAPI, Boto3) with GPU allocation and model inference at scale",
        "Architected data pipelines integrating LLM inference with social APIs — thousands of concurrent requests with circuit-breaking",
        "Built NLP pipelines for tokenisation, NER, sentiment analysis, and intent classification",
        "Implemented automated model performance monitoring to detect data drift and inference degradation in production"
      ]
    },
    {
      role: "Head of Engineering",
      company: "TajiLabs",
      logo: "assets/logos/tajilabs.png",
      date: "Jan 2019 – Present",
      desc: "I lead a technology consultancy delivering cloud infrastructure, DevOps transformation, and custom software for SMEs and enterprises across East Africa.",
      points: [
        "Introduced sprint discipline and Jira workflows; formalised roles, reporting, and performance KPIs across engineering",
        "Owned cloud architecture and SRE for 25+ clients across AWS, GCP, and Azure using Kubernetes, Docker, and Terraform",
        "Designed ERP, financial, logistics, and fleet platforms — Jengo, Verifay, TajiTrack — plus AI-integrated GRN automation for Bio Foods",
        "Grew the consultancy so platforms collectively support 500–1,000+ active users across East Africa"
      ]
    },
    {
      role: "Full Stack Engineer",
      company: "OboTech Solutions Ltd",
      logo: "assets/logos/obotech.png",
      date: "Jul 2019 – Apr 2022",
      desc: "Delivered digital solutions across fintech, enterprise, and logistics; progressed from Junior Web Developer to project lead.",
      points: [
        "Led CompactHydro — cloud-based Request Management System (REST APIs, PostgreSQL, React)",
        "Built SACCO/MFI platforms including reconciliation, transaction processing, and financial reporting",
        "Designed ApolloTours ERP — booking flow, vehicle allocation, and customer management",
        "Deployed containerised applications with Docker and automated CI/CD across PHP, JavaScript, Java, Python, Go, React, Vue, and Flutter"
      ]
    },
    {
      role: "IT Support Specialist",
      company: "ICT Authority",
      logo: "assets/logos/icta.png",
      date: "Jun 2020 – Oct 2020",
      desc: "Apprenticeship supporting county government office infrastructure in Siaya.",
      points: [
        "Maintained IT infrastructure, managed data backup and recovery, and deployed new devices",
        "Provided technical support to departmental staff and government officers; liaised with external partners",
        "Built the operations fundamentals and stakeholder communication that translate into production engineering"
      ]
    }
  ];

  const fullEducation = [
    { degree: "MSc. Computing and Information Systems", school: "Strathmore University", date: "Ongoing", desc: "Focus: Enterprise Information Systems, Scalable Data Architectures, and Applied Systems Engineering." },
    { degree: "BSc. Computer Science", school: "Kibabii University", date: "2017 – 2022", desc: "Specialization: Software Engineering, Data Structures & Algorithms, Database Systems." },
    { degree: "DevOps Engineering Professional", school: "Moringa School", date: "2022 – 2023", desc: "Focus: CI/CD, Containerization, Cloud Deployment, Infrastructure Automation, RPA." }
  ];

  const certificationsData = [
    "Andela Certified DevOps Engineer",
    "AWS Solutions Architect",
    "Certified Kubernetes Native Associate (CKNA)"
  ];

  const communityEngagement = [
    { title: "Mentor — software engineering, system design, and career development", org: "Nairobi DevOps Community", date: "", icon: <Users size={16} /> },
    { title: "Machine Learning Co-Lead — workshops, hackathons, mentored 50+ students", org: "Google DSC, Kibabii University", date: "2020–2021", icon: <Award size={16} /> }
  ];

  const lifestyleInterests = [
    { item: "Cars & Turbo Builds", icon: <Car size={16} /> },
    { item: "Piano & Music Composition", icon: <Music size={16} /> },
    { item: "Tech for Social Impact", icon: <Zap size={16} /> }
  ];

  const personalContact = {
    phone: "+254 706 673 348",
    email: "me@omondijeff.dev",
    location: "Nairobi, Kenya"
  };

  const certificationBadges = [
    { id: 'aws-sap', src: '/badge-aws-sap.png', label: 'AWS Certified Solutions Architect – Professional' },
    { id: 'azure-sa', src: '/badge-azure-sa.png', label: 'Microsoft Certified: Azure Solutions Architect Expert' },
    { id: 'terraform', src: '/badge-terraform.png', label: 'HashiCorp Certified: Terraform Associate' },
    { id: 'cka', src: '/badge-cka.png', label: 'Certified Kubernetes Administrator (CKA)' },
    { id: 'kcna', src: '/badge-kcna.png', label: 'Kubernetes and Cloud Native Associate (KCNA)' }
  ];

  const nextSection = () => currentSection < 3 && setCurrentSection(c => c + 1);
  const prevSection = () => currentSection > 0 && setCurrentSection(c => c - 1);

  const springConfig = { type: "spring", stiffness: 60, damping: 15, mass: 0.8 };

  return (
    <div className="main-canvas">
      <Seo title={SITE_TITLE} description={SITE_DESCRIPTION} path="/" image="/og/home.png" />
      <SiteNav mode="home" currentSection={currentSection} onSection={setCurrentSection} />

      <motion.div 
        className="persistent-subject-wrapper"
        animate={{ 
          x: (isWorksMobile && currentSection === 0) ? "0" :
             (isWorksMobile && currentSection !== 0) ? '-130%' :
             currentSection === 0 ? "15%" :
             (currentSection === 1 || currentSection === 2) ? "-32%" :
             "-22%",
          scale: (currentSection === 0) ? 1 :
                 (currentSection === 2) ? 0.72 :
                 currentSection === 3 ? 0.7 : 0.85,
          opacity: (isWorksMobile && currentSection === 0) ? 0.5 :
                   (isWorksMobile && currentSection !== 0) ? 0 :
                    currentSection === 1 ? 0.85 :
                    currentSection === 2 ? 0.9 :
                    currentSection === 3 ? 0.65 : 1
        }}
        style={{ originY: 1 }}
        transition={springConfig}
      >
        <img
          src={currentSection === 3 ? '/subject-about.png' : '/subject-hero.png'}
          alt="Jeffrey Omondi"
          className="subject-img"
        />
      </motion.div>

      <AnimatePresence>
        {currentSection === 1 && (
          <motion.div 
            className="works-subject-overlay"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ delay: 0.3 }}
          >
            <div className="belief-tag">I believe</div>
            <h3 className="belief-text">"I believe in building systems that don't just work, but evolve—bridging the gap between robust infrastructure and intelligent software."</h3>
            
            <div className="roles-floating-list">
              {experienceStats.map((role, idx) => (
                <div key={idx} className="role-floating-item glass-premium mono">
                  {role.label}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {currentSection === 2 && !isWorksMobile && (
          <motion.div 
            className="services-badges-overlay"
            initial={{ opacity: 0, y: 30, x: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, x: -20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
          >
            <div className="badge-grid">
              {certificationBadges.map((badge, bidx) => (
                <motion.div 
                  key={badge.id}
                  className="badge-item glass-premium"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + bidx * 0.1 }}
                >
                  <img src={badge.src} alt={badge.label} title={badge.label} />
                </motion.div>
              ))}
            </div>
            <div className="badge-tag mono uppercase">Certified Engineering</div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div 
        className="sections-container"
        drag="x"
        dragDirectionLock={true}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.05}
        onDragEnd={(e, { offset, velocity }) => {
          const swipeThreshold = 50;
          if (offset.x < -swipeThreshold) {
            nextSection();
          } else if (offset.x > swipeThreshold) {
            prevSection();
          }
        }}
        animate={{ x: `-${currentSection * 100}%` }}
        transition={{ duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
      >
        {/* SECTION 0: HERO */}
        <section className="section hero">
          <div className="grain-overlay" />
          
          {techLogos.map((logo, idx) => (
            <motion.div 
              key={idx}
              style={{ 
                position: 'absolute', 
                top: logo.top, 
                left: logo.left, 
                right: logo.right, 
                zIndex: 3,
                width: 44,
                height: 44,
                overflow: 'hidden',
                pointerEvents: 'none'
              }}
              animate={{ y: [0, idx % 2 === 0 ? -12 : 12, 0], opacity: [0.4, 0.6, 0.4] }}
              transition={{ repeat: Infinity, duration: 8 + idx, ease: "easeInOut" }}
            >
              <img src={logo.src} alt={logo.name} width={44} height={44} style={{ width: 44, height: 44, objectFit: 'contain', filter: 'grayscale(80%) opacity(0.4)', display: 'block' }} />
            </motion.div>
          ))}

          <AnimatePresence>
            {currentSection === 0 && (
              <motion.div 
                className="hero-content" 
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
              >
                <p className="hello">Hello, I'm</p>
                <h1 className="jeffrey-name grad-text">JEFFREY<br/>OMONDI</h1>
                <div className="intro-text">
                  <div className="typewriter-container">
                    <Typewriter texts={["DevOps Engineer", "AI Automation Specialist"]} />
                  </div>
                  <p className="intro-sub">DevOps and AI automation for production systems at scale.</p>
                </div>
                
                <div className="hero-logos-ribbon">
                  <span className="mono uppercase small">Proven Impact At</span>
                  <div className="hero-logos-mask">
                    <div className="hero-logos-scroll">
                      {(() => {
                        const ribbonJobs = fullExperience.filter((job) => job.company !== 'Turality LLC');
                        return [...ribbonJobs, ...ribbonJobs].map((job, idx) => (
                          <img key={idx} src={job.logo} alt={job.company} className="hero-logo-item" title={job.company} />
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>


          <div className="hero-footer-right">
            <div className="socials">
              <a href="mailto:jeffomondi.eng@gmail.com" className="social-circle blue"><Mail size={24} /></a>
              <a href="https://github.com/raburujeff" className="social-circle"><GithubIcon size={24} /></a>
              <a href="https://linkedin.com/in/omondijeff" className="social-circle"><LinkedinIcon size={24} /></a>
            </div>
          </div>
        </section>

        <div className="section works-view">
          <div className="works-scroll-container scroll-y">
            <div className="works-header-sticky">
               <span className="works-count mono uppercase">Archive ({filteredProjects.length})</span>
               <h2 className="section-title grad-text works-section-title">RECENT <span>WORKS</span></h2>
               
               <div className="works-filter-bar">
                 {["ALL", "WEB", "PRODUCTS", "AUTOMATION", "DEVOPS", "SHOPIFY"].map(f => (
                   <span 
                    key={f} 
                    className={`filter-pill mono ${activeFilter === f ? 'active' : ''}`}
                    onClick={() => setActiveFilter(f)}
                   >
                     {f}
                   </span>
                 ))}
               </div>
            </div>

            <div className="works-full-grid">
              {filteredProjects.map((w, idx) => (
                <motion.div 
                  key={w.id || idx} 
                  layout
                  className="work-card-premium" 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ y: -10, scale: 1.01 }}
                >
                  <div className="work-thumb-p">
                    <img src={w.img} alt={w.title} />
                  </div>
                  <div className="work-info-p">
                    <div className="work-top">
                      <span className="work-year mono">{w.year}</span>
                      <span className="work-meta mono uppercase">{w.cat}</span>
                    </div>
                    <h3 className="mono">{w.title}</h3>
                    <p>{w.desc}</p>
                    <div className="work-tech-tags">
                      {w.tech?.map((t, tidx) => (
                        <span key={tidx} className="w-tech-tag mono">{t}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* SECTION 2: SERVICES */}
        <div className="section services-view">
          <div className="services-scroll-container scroll-y">
            <div className="services-header-sticky">
              <div className="services-header-lead">
                <span className="works-count mono uppercase">
                  {servicesFilter === "STACK" ? `Stack (${expertiseCategories.length})` : `Capabilities (${serviceCapabilities.length})`}
                </span>
                <h2 className="section-title grad-text services-section-title">
                  {servicesFilter === "STACK" ? <>TECHNICAL <span>STACK</span></> : <>WHAT I <span>DELIVER</span></>}
                </h2>
              </div>
              <div className="services-header-aside">
                <p className="service-intro">
                  {servicesFilter === "STACK"
                    ? "The tools behind the work — still not a keyword dump."
                    : "Engagements you can hire for. Flip to Stack for the tooling depth."}
                </p>
                <div className="works-filter-bar services-filter-bar">
                  {["CAPABILITIES", "STACK"].map((f) => (
                    <span
                      key={f}
                      className={`filter-pill mono ${servicesFilter === f ? "active" : ""}`}
                      onClick={() => setServicesFilter(f)}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
             
             <div className="services-full-grid">
               {servicesCards.map((cat, idx) => (
                 <motion.div 
                   key={`${servicesFilter}-${cat.id}`} 
                   className={`expertise-card${idx === 0 ? ' expertise-card--lead' : ''}`}
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: idx * 0.05 }}
                   whileHover={{ y: -10, scale: 1.01 }}
                 >
                   <div className="expertise-card-main">
                     <div className="expertise-card-meta">
                       <span className="expertise-index mono">{String(idx + 1).padStart(2, '0')}</span>
                       <div className="expertise-icon-wrapper">{cat.icon}</div>
                     </div>
                     <div className="expertise-card-copy">
                       <h3>{cat.title}</h3>
                       <p className="expertise-blurb">{cat.blurb}</p>
                     </div>
                   </div>
                   {cat.groups ? (
                     <div className="skill-groups">
                       {cat.groups.map((group) => (
                         <div key={group.label} className="skill-group">
                           <span className="skill-group-label mono">{group.label}</span>
                           <div className="skills-pill-cloud">
                             {group.items.map((skill) => (
                               <span key={skill} className="skill-pill">{skill}</span>
                             ))}
                           </div>
                         </div>
                       ))}
                     </div>
                   ) : (
                     <div className="skills-pill-cloud">
                       {cat.skills.map((skill) => (
                         <span key={skill} className="skill-pill">{skill}</span>
                       ))}
                     </div>
                   )}
                 </motion.div>
               ))}
             </div>
          </div>
        </div>

        {/* SECTION 3: ABOUT */}
        <div className="section about-view scroll-y">
          <div className="grain-overlay" />
          
          <div className="about-header-sticky">
            <img
              src="/subject-about.png"
              alt="Jeffrey Omondi"
              className="about-mobile-portrait"
            />
            <div className="about-header-lead">
              <span className="works-count mono uppercase">Perspective</span>
              <h2 className="section-title grad-text">BUILDING <span>FOUNDATIONS</span></h2>
            </div>
            <p className="service-intro">
              DevOps Engineer and AI Automation Specialist with 5+ years designing, building, and operating production systems across East Africa and internationally.
            </p>
          </div>

          <div className="about-modern-layout">
            <div className="about-intro-grid">
              <div className="main-bio">
                <div className="bio-sub">
                    <p>
                      My early days in tech with <strong>ICTA Kenya</strong> taught me a fundamental truth: <strong>software is only as reliable as the foundation it’s built on.</strong>
                      That gap between user frustration and technical debt is why I stopped just fixing systems and started engineering them for resilience.
                    </p>
                    <p>
                      I founded <strong>TajiLabs</strong>, a consultancy now serving <strong>25+ clients</strong> across East Africa — multi-tenant SaaS like <strong>Verifay</strong> and <strong>Jengo</strong>, plus Kubernetes and Terraform for client platforms.
                      DevOps here is a <strong>business strategy</strong> for speed and stability, not a job title.
                    </p>
                    <p>
                      At <strong>Greenspoon</strong> I reported to the CTO and CEO through 96% operational growth: an <strong>Internal Developer Platform</strong> that cut deploys from hours to minutes, and <strong>Greenspoon Paperless</strong> for warehouse fulfilment.
                      At <strong>BetterQA</strong> I still scale enterprise QA SaaS — multi-tenant Laravel, Terraform on AWS/GCP, and Kubernetes test orchestration.
                    </p>
                    <p>
                      Today I am <strong>Systems Engineering Lead Consultant at Aberdair Aviation Group</strong>, delivering a greenfield internal platform through architecture, containerised environments, and production-ready IaC.
                      Whether shipping APIs or cutting over infrastructure, the work is the same: systems that don't just work, but <strong>evolve</strong>.
                    </p>
                  </div>
                </div>
                <div className="about-stats-bento">
                  <div className="a-stat-bento glass-premium">
                    <span className="stat-num grad-text">5+</span>
                    <span className="stat-label-b">Years Exp</span>
                  </div>
                  <div className="a-stat-bento glass-premium">
                    <span className="stat-num grad-text">50+</span>
                    <span className="stat-label-b">Projects</span>
                  </div>
                  <div className="a-stat-bento glass-premium">
                    <span className="stat-num grad-text">25+</span>
                    <span className="stat-label-b">Core Clients</span>
                  </div>
              </div>
            </div>

            <div className="about-content-main">
              <div className="about-timeline-column">
                <h3 className="column-title mono uppercase"><Briefcase size={20} /> Professional Journey</h3>
                <div className="vertical-timeline">
                  {fullExperience.map((job, idx) => (
                    <div key={idx} className="v-timeline-item">
                      <div className="v-marker"></div>
                      <div className="v-content glass-premium">
                        <div className="v-header">
                          <span className="v-date mono">{job.date}</span>
                          <div className="v-role-brand">
                            <h4>{job.role}</h4>
                            <img src={job.logo} alt={job.company} className="v-company-logo" />
                          </div>
                          <p className="v-company">{job.company}</p>
                        </div>
                        <ul className="v-points">
                          {job.points.map((p, pi) => <li key={pi}>{p}</li>)}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="about-secondary-column">
                <div className="edu-section-block">
                  <h3 className="column-title mono uppercase"><GraduationCap size={20} /> Education</h3>
                  {fullEducation.map((edu, idx) => (
                    <div key={idx} className="edu-item-compact glass-premium">
                      <h5>{edu.degree}</h5>
                      <p>{edu.school}</p>
                      <span className="edu-date mono">{edu.date}</span>
                    </div>
                  ))}
                </div>

                <div className="cert-section-block">
                  <h3 className="column-title mono uppercase"><Award size={20} /> Certifications</h3>
                  <div className="cert-grid-compact">
                    {certificationsData.map((cert, idx) => (
                      <div key={idx} className="cert-item-tag glass-premium">{cert}</div>
                    ))}
                  </div>
                </div>

                <div className="comm-section-block">
                  <h3 className="column-title mono uppercase"><Users size={20} /> Community</h3>
                  {communityEngagement.map((comm, idx) => (
                    <div key={idx} className="comm-item-compact border-left-grad">
                      <div className="comm-head">{comm.icon} <span>{comm.org}</span></div>
                      <p>{comm.title}</p>
                    </div>
                  ))}
                </div>

                <div className="lifestyle-section-block">
                  <h3 className="column-title mono uppercase"><Zap size={20} /> Beyond Code</h3>
                  <div className="lifestyle-grid-compact">
                    {lifestyleInterests.map((life, idx) => (
                      <div key={idx} className="life-item-compact glass-premium">
                        {life.icon} <span>{life.item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="about-footer-connect glass-premium">
               <h3 className="grad-text">Let's connect and build the future.</h3>
               <div className="contact-pills">
                 <span className="c-pill"><Mail size={16} /> {personalContact.email}</span>
                 <span><Smartphone size={16} /> {personalContact.phone}</span>
                 <span><MapPin size={16} /> {personalContact.location}</span>
               </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Side nav (desktop) */}
      <AnimatePresence>
        {currentSection < 3 && (
          <motion.div 
            className="nav-control next" 
            onClick={nextSection}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <div className="nav-control-btn">
              <ChevronRight size={24} />
            </div>
            <div className="nav-control-label mono uppercase small">
              Next: {currentSection === 0 ? "WORKS" : currentSection === 1 ? "SERVICES" : "ABOUT"}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {currentSection > 0 && (
          <motion.div 
            className="nav-control prev" 
            onClick={prevSection}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="nav-control-btn">
              <ChevronLeft size={24} />
            </div>
            <div className="nav-control-label mono uppercase small">
              Back: {currentSection === 1 ? "HOME" : currentSection === 2 ? "WORKS" : "SERVICES"}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating swipe dock — teaches horizontal navigation */}
      <div className="section-swipe-dock-wrap">
        <div className="section-swipe-dock">
          <button
            type="button"
            className="swipe-dock-arrow"
            onClick={prevSection}
            disabled={currentSection === 0}
            aria-label="Previous section"
          >
            <ChevronLeft size={16} strokeWidth={2.4} />
          </button>

          <div className="swipe-dock-track" role="tablist" aria-label="Sections">
            {[
              { id: 0, label: "Home" },
              { id: 1, label: "Works" },
              { id: 2, label: "Services" },
              { id: 3, label: "About" },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={currentSection === s.id}
                className={`swipe-dock-seg ${currentSection === s.id ? "active" : ""}`}
                onClick={() => setCurrentSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="swipe-dock-arrow"
            onClick={nextSection}
            disabled={currentSection === 3}
            aria-label="Next section"
          >
            <ChevronRight size={16} strokeWidth={2.4} />
          </button>
        </div>
        <p className="swipe-dock-cue mono uppercase">Drag or swipe to explore</p>
      </div>
    </div>
  );
};

export default App;
